import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface PhotoCarouselProps {
  images: string[];
  altTextPrefix?: string;
  onImageClick?: (imageUrl: string) => void;
  className?: string;
}

const PhotoCarousel: React.FC<PhotoCarouselProps> = ({
  images,
  altTextPrefix = 'Photo',
  onImageClick,
  className = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) {
    return null;
  }

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const handleImageClick = () => {
    if (onImageClick) {
      onImageClick(images[currentIndex]);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Main Image Container */}
      <div className="relative w-full bg-surface-subtle rounded-lg overflow-hidden">
        <div 
          className="flex items-center justify-center min-h-[300px] max-h-[500px] cursor-pointer"
          onClick={handleImageClick}
        >
          <img
            src={images[currentIndex]}
            alt={`${altTextPrefix} ${currentIndex + 1}`}
            className="max-w-full max-h-[500px] object-contain"
          />
        </div>

        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full p-2 transition-all z-10"
              aria-label="Previous image"
            >
              <ChevronLeftIcon className="h-6 w-6" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full p-2 transition-all z-10"
              aria-label="Next image"
            >
              <ChevronRightIcon className="h-6 w-6" />
            </button>
          </>
        )}

        {/* Image Counter */}
        {images.length > 1 && (
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-sm px-3 py-1 rounded-full">
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>

      {/* Thumbnail Indicators */}
      {images.length > 1 && (
        <div className="flex gap-2 mt-4 justify-center overflow-x-auto pb-2">
          {images.map((image, index) => (
            <button
              data-track="photo_thumbnail"
              key={index}
              onClick={() => goToSlide(index)}
              className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all ${
                index === currentIndex
                  ? 'border-primary-500 ring-2 ring-primary-200'
                  : 'border-edge-input hover:border-edge opacity-75 hover:opacity-100'
              }`}
              aria-label={`Go to ${altTextPrefix} ${index + 1}`}
            >
              <img
                src={image}
                alt={`${altTextPrefix} ${index + 1} thumbnail`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoCarousel;





